import {
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useContext,
  type ReactNode,
} from "react";
import { createElement } from "react";
import { navigateDateRange as calcDateRange } from "../utils/dateRange";

// =============================================================================
// useGlobalDateRange - SHARED DATE RANGE STATE (React Context)
// =============================================================================
// PURPOSE: Single source of truth for date range across all pages.
//
// REQUIREMENTS:
// - Persists to localStorage on every change
// - Loads from localStorage on mount
// - Shared across: Dashboard, Transactions, Leaderboard, DM Analytics,
//   Pre-Call Compliance, Winners, Installments, Calendly
// - Default: Last 30 days (today - 29 days to today)
//
// KEY DECISIONS:
// - Uses a single localStorage key for all pages
// - Stores ISO strings, converts to Date objects on read
// - No org scoping (date range is user preference, not org data)
// - Context (not per-instance useState) so all consumers share ONE state silo.
//   Before this was a plain useState hook, every call site got an independent
//   silo — picker change only updated that silo. Other silos re-read from
//   localStorage only on hard refresh, causing the "must change AND refresh" bug.
// - Throws if used outside <DateRangeProvider> — fails loudly, not silently.
//   The old silent fallback WAS the bug class.
// =============================================================================

export const GLOBAL_DATE_RANGE_KEY = "altara_global_date_range";

interface DateRange {
  start: Date | null;
  end: Date | null;
  isAllTime?: boolean;
}

interface StoredDateRange {
  start: string | null;
  end: string | null;
  isAllTime?: boolean;
}

/**
 * Load date range from localStorage, with fallback to last 30 days
 */
function loadDateRange(): DateRange {
  try {
    const stored = localStorage.getItem(GLOBAL_DATE_RANGE_KEY);
    if (stored) {
      const parsed: StoredDateRange = JSON.parse(stored);
      return {
        start: parsed.start ? new Date(parsed.start) : null,
        end: parsed.end ? new Date(parsed.end) : null,
        isAllTime: parsed.isAllTime === true,
      };
    }
  } catch {
    // Ignore parse errors
  }

  // Default: last 30 days (NOT All Time — no reason to change first-load default per D10)
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { start, end, isAllTime: false };
}

/**
 * Custom event name dispatched when date range changes.
 * TransactionContext listens for this to expand its prefetch window.
 */
export const DATE_RANGE_CHANGE_EVENT = "altara-date-range-change";

/**
 * Save date range to localStorage
 */
function saveDateRange(range: DateRange): void {
  try {
    const toStore: StoredDateRange = {
      start: range.start?.toISOString() || null,
      end: range.end?.toISOString() || null,
      isAllTime: range.isAllTime === true,
    };
    localStorage.setItem(GLOBAL_DATE_RANGE_KEY, JSON.stringify(toStore));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DateRangeContextValue {
  dateRange: DateRange;
  setDateRange: (value: DateRange | ((prev: DateRange) => DateRange)) => void;
  navigateDateRange: (direction: "prev" | "next") => void;
}

const DateRangeContext = createContext<DateRangeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider — mount once at App.tsx root
// ---------------------------------------------------------------------------

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [dateRange, setDateRangeState] = useState<DateRange>(loadDateRange);
  // Persist + broadcast on every state change — single write path
  useEffect(() => {
    saveDateRange(dateRange);
    window.dispatchEvent(new Event(DATE_RANGE_CHANGE_EVENT));
  }, [dateRange]);

  // Cross-tab sync — storage event fires in OTHER tabs when this tab writes.
  // Lets a picker change in tab A propagate to tab B.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === GLOBAL_DATE_RANGE_KEY) {
        setDateRangeState(loadDateRange());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setDateRange = useCallback((value: DateRange | ((prev: DateRange) => DateRange)) => {
    setDateRangeState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return next;
    });
  }, []);

  const navigateDateRange = useCallback((direction: "prev" | "next") => {
    setDateRangeState((current) => {
      const result = calcDateRange(current, direction);
      // If navigation returns null (no valid range), keep current
      return result ?? current;
    });
  }, []);

  const value = useMemo(
    () => ({
      dateRange,
      setDateRange,
      navigateDateRange,
    }),
    [dateRange, setDateRange, navigateDateRange]
  );

  return createElement(DateRangeContext.Provider, { value }, children);
}

// ---------------------------------------------------------------------------
// Hook — same public API as before
// ---------------------------------------------------------------------------

/**
 * Shared hook for global date range state.
 * Must be used within <DateRangeProvider> (mounted in App.tsx).
 * Returns: { dateRange, setDateRange, navigateDateRange }
 */
export function useGlobalDateRange(): DateRangeContextValue {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error(
      "useGlobalDateRange must be used within <DateRangeProvider>. Mount it at App.tsx near the root."
    );
  }
  return ctx;
}

export type { DateRange };
